<template>
    <component
        v-if="!$root.edit && visible && lazy"
        ref="c"
        v-intersect="{handler: onIntersect}"
        v-bind:is="config.component"
        v-bind="props"
        :class="classes"
        :style="styles"
        :id="id"
        v-on:action="forwardEvent"
        @triggerProps="triggerProps"
    />

    <component
        v-else-if="!$root.edit && visible && !lazy"
        ref="c"
        v-bind:is="config.component"
        v-bind="props"
        :class="classes"
        :style="styles"
        :id="id"
        v-on:action="forwardEvent"
        @triggerProps="triggerProps"
    />
    <div v-else-if="!$root.edit && !visible"></div>

    <div v-else-if="$root.edit && isVueComponent && hasErrors" @click.stop="select">
        <div class="zoezi-vue-component-error">
            Något gick fel när komponenten skulle läsas in. Fel i koden?
        </div>
        <component
            ref="c"
            :key="renderKey"
            v-ripple="reallyRipple ? {class: 'zoezi-edit-hover-component'} : false"
            @click.native.stop="select"
            v-bind:is="config.component"
            v-bind="props"
            v-on:save="saveChildProps"
            v-on:silentsave="silentSaveChildProps"
            :id="id"
            v-on:select="onSelect"
            :class="classes"
            :style="styles"
            v-on:action="forwardEvent"
            @triggerProps="triggerProps"
        />
    </div>

    <!-- Edit mode -->

    <component
        v-else
        ref="c"
        :key="renderKey"
        class="z-component"
        v-ripple="reallyRipple ? {class: 'zoezi-edit-hover-component'} : false"
        @click.native.stop="select"
        v-bind:is="config.component"
        v-bind="props"
        v-on:save="saveChildProps"
        v-on:silentsave="silentSaveChildProps"
        :id="id"
        v-on:select="onSelect"
        :class="classes"
        :style="styles"
        v-on:action="forwardEvent"
        @triggerProps="triggerProps"
    >
        <slot v-for="slot in Object.keys($slots)" :name="slot" :slot="slot"/>
    </component>
</template>
<script>
import Vue from 'vue'
import components from '../component.js'
import { componentVisibleFunction } from '../utils/component.js';
export default {
    name: 'ComponentWrapper',

    props: {
        config: {
            type: Object
            // {
            //   component: String
            //   props: Object
            //   style: Object
            //   id: String
            // }
        },
        canRemove: {
            type: Boolean,
            default: false
        },
        array: {
            type: Array
        },
        ripple: {
            type: Boolean,
            default: true
        }
    },

    data: () => ({
        renderKey: 0,
        hasErrors: false,
        lazyModel: false,
        propsKey: 0
    }),
    watch: {
        'config.style'() {
            this.update();
        },
        '$root.edit': {
            immediate: true,
            handler() {
                this.detectErrors();
            }
        }
    },
    computed: {
        id() {
            return this.config.component == 'zoezi-root' ? 'app' : this.config.id;
        },
        isSelected() {
            return this.$root.edit && this.$store.state.editComponent && this.$store.state.editComponent == this.$refs.c;
        },
        reallyRipple() {
            return this.ripple && this.$root.edit && (!this.$store.state.editComponent && this.$store.state.editComponent != this.$refs.c) && this.isAncestorOfPage(this);
        },
        classes() {
            let classes = [];

            if (this.isSelected) {
                classes.push('zoezi-edit-hover-component');

                //classes.push('animate__animated');
                //classes.push('animate__pulse');
            }

            if (this.config?.style?.fillHeight) {
                classes.push('fill-height');
            }

            if (this.config.component && this.config.component.startsWith('zoezi-')) {
                classes.push(this.config.component);
            }

            let showNow = !this.lazy || this.lazyModel;
            if (this.config?.style?.animation && showNow) {
                classes.push('animate__animated');
                classes.push('animate__' + this.config.style.animation)
            }

            if (this.config?.style?.classes) {
                classes.push(this.config?.style?.classes);
            }

            if (this.lazy && !this.lazyModel) {
                classes.push('z-lazy-invisible');
            }

            if (this.config.style?.color && !this.config.style.color.startsWith('#')) {
                classes.push(this.config.style.color + '--text');
            }

            if (this.config.style?.backgroundColor && !this.config.style.backgroundColor.startsWith('#')) {
                classes.push(this.config.style.backgroundColor);
            }

            return classes;
        },
        styles() {
            let style = this.config.style;
            if (style) {
                let styles = {};
                if (style.backgroundColor && style.backgroundColor.startsWith('#')) {
                    styles['background-color'] = this.$root.colorToHex(style.backgroundColor) + ' !important';
                }

                if (style.color && style.color.startsWith('#')) {
                    styles.color = this.$root.colorToHex(style.color) + ' !important';
                }

                if (style.textAlign) {
                    let align = {'justify-space-around': 'justify', 'justify-start': 'left', 'justify-center': 'center', 'justify-end': 'right'}[style.textAlign];
                    styles['text-align'] = align + '!important';
                }

                let margins = ['marginTop', 'marginRight', 'marginBottom', 'marginLeft']
                margins.forEach(margin => {
                    if (style[margin] !== undefined) {
                        styles[margin] = style[margin] + 'px !important';
                    }
                });

                let paddings = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft']
                paddings.forEach(padding => {
                    if (style[padding] !== undefined) {
                        styles[padding] = style[padding] + 'px !important';
                    }
                });

                return styles;
            }

            return null;
        },
        isVueComponent() {
            return this.config && this.config.component == 'zoezi-vue-component';
        },
        visibleFunction() {
            return componentVisibleFunction(this.config, this.props);
        },
        visible() {
            return this.$root.edit || !this.visibleFunction || this.visibleFunction();
        },
        lazySupport() {
            return 'IntersectionObserver' in window && 'IntersectionObserverEntry' in window && 'intersectionRatio' in window.IntersectionObserverEntry.prototype;
        },
        lazy() {
            return this.lazySupport && this.config?.style?.lazy && !this.$root.edit;
        },
        props() {
            this.propsKey; // Reference it!

            if (this.config?.style?.pageParams) {
                let cprops = components.getComponentProps(this.config.component);
                let props = {...this.config.props};
                this.config.style.pageParams.forEach(p => {
                    let value = this.$route.params[p.id] ?? this.$route.query[p.id];
                    if (value === undefined) return;
                    props[p.prop] = value;
                    let cprop = cprops.find(cp => cp.name == p.prop)
                    if (cprop && !isNaN(props[p.prop]) && (cprop.type == 'number' || (cprop.type == 'select' && !cprop.typeinfo.multiple))) {
                        props[p.prop] = Number(props[p.prop]);
                    }

                    else if (cprop && cprop.type == 'select' && cprop.typeinfo && cprop.typeinfo.multiple) {
                        props[p.prop] = props[p.prop] ? props[p.prop].split(',') : []
                        if (!props[p.prop].find(x => isNaN(x))) {
                            props[p.prop] = props[p.prop].map(x => Number(x))
                        }
                    }

                    else if (cprop && cprop.type == 'checkbox') {
                        props[p.prop] = props[p.prop] === 'true';
                    }
                })

                if (this.$refs.c?.overrideProps) {
                    Object.assign(props, this.$refs.c.overrideProps)
                }

                return props;
            }

            if (this.$refs.c?.overrideProps) {
                return Object.assign({}, this.config.props, this.$refs.c.overrideProps)
            }

            return this.config.props;
        }
    },
    methods: {
        saveChildProps(props) {
            if (props) {
                Object.assign(this.config.props, props);
            }
            this.save();

            this.$store.commit('setEditComponent', null);
            Vue.nextTick(() => {
                this.select();
            });
        },
        save() {
            this.$root.saveHomePage();
            this.update();
        },
        update() {
            if (this.config.component != 'zoezi-root') {
                ++this.renderKey;
                this.detectErrors();
            }
        },
        silentSaveChildProps(props) {
            if (props) {
                Object.keys(props).forEach(key => {
                    Vue.set(this.config.props, key, props[key]);
                });
                this.propsKey++;
            }
            this.$root.saveHomePage();
        },
        onSelect(component) {
            this.$store.commit('setEditComponent', component);
        },
        remove() {
            if (this.array) {
                let index = this.array.indexOf(this.config);
                if (index != -1) {
                    this.array.splice(index, 1);
                    this.save();
                }
            } else {
                // FIXME: Get rid of this (only used by App.vue and MenuItem)
                this.$emit('remove');
            }
        },
        forwardEvent() {
            this.$emit.apply(this, arguments);
        },
        select() {
            if (this.$refs.c.$options.name == 'zoezi-root') {
                return;
            }
            this.$store.commit('setEditComponent', this.$refs.c);
        },
        detectErrors() {
            if (this.isVueComponent && this.$root.edit) {
                Vue.nextTick(() => {
                    let c = this.$refs.c;
                    if (c) {
                        let rect = c.$el.getBoundingClientRect ? c.$el.getBoundingClientRect() : {width: 0, height: 0};
                        this.hasErrors = !rect.width || !rect.height;
                    }
                });
            }
        },
        onIntersect(entries) {
            if (entries && entries.length == 1 && entries[0].isIntersecting && !this.lazyModel) {
                this.lazyModel = true;
            }
        },
        triggerProps() {
            this.propsKey++;
        },
        isAncestorOfPage(x) {
            if (!x) {
                return null;
            }

            if (x.config && x.component == 'zoezi-page') {
                return true;
            }

            return this.isAncestorOfPage(x.$parent);
        }
    }
}
</script>
